import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Search, X, SlidersHorizontal, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";

export default function Catalog() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const [sort, setSort] = useState(params.get("sort") || "default");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    setVisibleCount(12);
    const q = new URLSearchParams();
    if (slug) q.set("category", slug);
    if (search) q.set("search", search);
    q.set("sort", sort);
    api.get(`/products?${q.toString()}`).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [slug, search, sort]);

  const activeCat = cats.find((c) => c.slug === slug);
  const title = activeCat ? activeCat.name : "Catálogo completo";

  const Sidebar = ({ inDrawer = false }) => (
    <div className={`space-y-8 ${inDrawer ? "p-6" : ""}`}>
      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-secondary" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar produtos..."
          className="w-full pl-11 pr-10 py-3 rounded-full bg-white border border-brand-primary/10 focus:border-brand-primary focus:outline-none font-tech text-sm shadow-[0_8px_24px_-12px_rgba(15,15,15,0.1)]"
          data-testid="search-input"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1" data-testid="search-clear">
            <X size={16} />
          </button>
        )}
      </div>

      {/* Categories */}
      <div>
        <h3 className="font-tech text-xs uppercase tracking-[0.25em] text-ink-secondary mb-4">Categorias</h3>
        <ul className="space-y-1.5">
          <li>
            <Link
              to="/loja"
              onClick={() => setMobileFiltersOpen(false)}
              className={`flex items-center justify-between px-4 py-2.5 rounded-full text-sm font-tech transition ${
                !slug ? "bg-brand-primary text-white shadow-[0_8px_24px_-10px_rgba(15,15,15,0.4)]" : "hover:bg-white"
              }`}
              data-testid="filter-all"
            >
              <span>Todos os produtos</span>
            </Link>
          </li>
          {cats.filter((c) => c.slug !== "sem-categoria").map((c) => (
            <li key={c.slug}>
              <Link
                to={`/categoria/${c.slug}`}
                onClick={() => setMobileFiltersOpen(false)}
                className={`flex items-center justify-between px-4 py-2.5 rounded-full text-sm font-tech transition ${
                  slug === c.slug ? "bg-brand-primary text-white shadow-[0_8px_24px_-10px_rgba(15,15,15,0.4)]" : "hover:bg-white"
                }`}
                data-testid={`filter-${c.slug}`}
              >
                <span>{c.name}</span>
                <span className={`text-xs ${slug === c.slug ? "text-white/70" : "text-ink-secondary"}`}>
                  {c.count}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Sort */}
      <div>
        <h3 className="font-tech text-xs uppercase tracking-[0.25em] text-ink-secondary mb-4">Ordenar por</h3>
        <div className="relative">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="appearance-none w-full px-4 py-3 pr-10 rounded-full bg-white border border-brand-primary/10 font-tech text-sm focus:outline-none focus:border-brand-primary"
            data-testid="sort-select"
          >
            <option value="default">Recomendado</option>
            <option value="price-asc">Menor preço</option>
            <option value="price-desc">Maior preço</option>
            <option value="name">Nome A-Z</option>
          </select>
          <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-ink-secondary" />
        </div>
      </div>

      {/* Trust card */}
      <div className="hidden lg:block p-5 rounded-3xl bg-gradient-to-br from-brand-primary to-brand-dark text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-brand-accent/20 blur-2xl" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-accent font-tech mb-2">PIX OFF</div>
          <div className="font-heading text-3xl mb-1">5% de desconto</div>
          <p className="text-xs text-white/70">Aplicado automaticamente no checkout.</p>
        </div>
      </div>
    </div>
  );

  // Category-specific custom banners. The image already contains the title/branding,
  // so we hide the default text overlay when a custom banner exists.
  const CATEGORY_BANNERS = {
    biobeauty: "/banners/biobeauty-banner.jpg",
    saude: "/banners/saude-banner.jpg",
    "bem-estar": "/banners/bem-estar-banner.jpg",
    fitness: "/banners/fitness-banner.jpg",
  };
  const customBanner = slug ? CATEGORY_BANNERS[slug] : null;

  // SEO data
  const path = slug ? `/categoria/${slug}` : "/loja";
  const seoTitle = activeCat ? `${activeCat.name} — Manipulados Premium` : "Catálogo · Todos os produtos";
  const seoDescription = activeCat
    ? `${activeCat.name} Biofarmax: ${products.length} produto${products.length !== 1 ? "s" : ""} manipulados com matérias-primas selecionadas. Entrega para todo o Brasil com qualidade farmacêutica.`
    : `Catálogo completo da Biofarmax com ${products.length} produtos manipulados — cápsulas, suplementos, dermocosméticos e fórmulas exclusivas. Entrega nacional.`;
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://biofarmax.com.br/" },
      { "@type": "ListItem", "position": 2, "name": "Loja", "item": "https://biofarmax.com.br/loja" },
      ...(activeCat
        ? [{ "@type": "ListItem", "position": 3, "name": activeCat.name, "item": `https://biofarmax.com.br/categoria/${activeCat.slug}` }]
        : []),
    ],
  };
  const collectionJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": title,
    "url": `https://biofarmax.com.br${path}`,
    "description": seoDescription,
    "isPartOf": { "@id": "https://biofarmax.com.br/#website" },
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": products.length,
      "itemListElement": products.slice(0, 12).map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `https://biofarmax.com.br/produto/${p.slug}`,
        "name": p.name,
      })),
    },
  };

  return (
    <div className="pt-20 sm:pt-24 pb-24 gradient-soft" data-testid="catalog-page">
      <SEO
        path={path}
        title={seoTitle}
        description={seoDescription}
        type="website"
        jsonLd={[breadcrumbJsonLd, collectionJsonLd]}
      />
      {/* Header strip */}
      {customBanner ? (
        <section
          className="relative overflow-hidden border-b border-brand-primary/8"
          data-testid="catalog-banner-custom"
        >
          <div
            className="w-full aspect-[1696/450] bg-no-repeat bg-bg-secondary"
            style={{
              backgroundImage: `url(${customBanner})`,
              backgroundSize: "contain",
              backgroundPosition: "center",
            }}
            aria-label={title}
          />
          {/* SR-only meta for SEO + product count below banner */}
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex items-center justify-between">
            <span className="sr-only"><h1>{title}</h1></span>
            <p className="text-xs sm:text-sm text-ink-secondary font-tech uppercase tracking-wider">
              {products.length} produto{products.length !== 1 && "s"} · matéria-prima selecionada
            </p>
          </div>
        </section>
      ) : (
        <section className="relative overflow-hidden border-b border-brand-primary/8">
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
               style={{ backgroundImage: "url(/generated/molecular-pattern.png)", backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="eyebrow mb-3">Catálogo</div>
              <h1 className="h-display text-5xl sm:text-6xl lg:text-7xl mb-4">{title}</h1>
              <p className="text-ink-secondary max-w-2xl">
                {products.length} produto{products.length !== 1 && "s"} manipulados com matérias-primas selecionadas e rigor farmacêutico.
              </p>
            </motion.div>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-10">
        {/* Mobile filter button */}
        <div className="lg:hidden mb-6 flex items-center justify-between gap-3">
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-brand-primary/10 font-tech text-sm"
            data-testid="mobile-filters-open"
          >
            <SlidersHorizontal size={16} /> Filtros
          </button>
          <div className="text-xs font-tech text-ink-secondary">{products.length} resultados</div>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-10">
          <aside className="hidden lg:block">
            <div className="sticky top-28"><Sidebar /></div>
          </aside>

          {/* Mobile drawer */}
          {mobileFiltersOpen && (
            <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filters-drawer">
              <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} />
              <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-bg-secondary overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-brand-primary/10">
                  <span className="font-heading text-xl">Filtros</span>
                  <button onClick={() => setMobileFiltersOpen(false)} data-testid="mobile-filters-close"><X size={20} /></button>
                </div>
                <Sidebar inDrawer />
              </div>
            </div>
          )}

          <div>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[3/4] rounded-3xl bg-white animate-pulse" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-brand-primary/10">
                <p className="text-ink-secondary mb-4">Nenhum produto encontrado.</p>
                <button onClick={() => setSearch("")} className="btn-outline" data-testid="clear-search">
                  Limpar busca
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5">
                  {products.slice(0, visibleCount).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
                {visibleCount < products.length && (
                  <div className="text-center mt-10">
                    <button
                      onClick={() => setVisibleCount((v) => v + 12)}
                      className="btn-outline"
                      data-testid="catalog-load-more"
                    >
                      Ver mais produtos ({products.length - visibleCount} restantes)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
